import { load } from "@arkadium/jarvis/dist/prebid";
import { getGeo } from "@arkadium/jarvis/dist/v2";
import { getSupportedMimes } from "../../utils/mimes";
import { JarvisResponse } from "../jarvis-service";
import { IBid, IProvider } from "../provider";
import { KeyValues } from "../keyvalues";

type PrebidAdUnit = { code: string, bids: any[] };
type RequestBidsOptions = { bidsBackHandler: any, adUnitCodes?: string[], timeout?: number };
type RequestBidsReturn = Promise<{ bids: any, timedOut: boolean, auctionId: string }>;
type BuildVideoUrlOptions = { adUnit: PrebidAdUnit, params?: any, bid?: any };
type PrebidInstance = {
  cmd: Array<() => void>;
  bidderSettings?: any;
  adUnits: any[];

  requestBids(opts: RequestBidsOptions): RequestBidsReturn;
  processQueue(): void;
  setConfig(config: any): void;
  addAdUnits(adUnits: any[]): void;

  markWinningBidAsUsed(options: { adUnitCode: string }): void;
  getBidResponsesForAdUnitCode(code: string): { bids: any[] };

  adServers: {
    dfp: {
      buildVideoUrl(options: BuildVideoUrlOptions): string;
    }
  }
};

const SKIPPABLE = 'skippable';
const UNSKIPPABLE = 'unskippable';

export class PrebidProvider implements IProvider {
  public name = 'prebid';
  protected prevAdId = '';
  protected bids: any[] = [];
  protected pbjsPromise!: ReturnType<typeof load>;
  protected geoPromise!: ReturnType<typeof getGeo>;

  protected iu: string;

  constructor(protected kv: KeyValues) {
    this.iu = `/100151972/${window.location.hostname}`;
  }

  public async configure(config: JarvisResponse) {
    this.iu = `/100151972${config.mcm ? ',' + config.mcm : ''}/${window.location.hostname}`;
    const { bids } = config?.videoConfig || {};
    this.bids = bids;
    return this.pbjsPromise = load(config);
  }

  public async requestBids(params: { duration: number }): Promise<IBid[]> {
    return new Promise(async (resolve, reject) => {
      setTimeout(() => resolve([]), 4000);
      const pbjs = await this.pbjsPromise;
      const geo = await this.geoPromise;
      const bids = this.bids.filter(b => !b.geo || new RegExp(b.geo).test(geo.country_code));
      pbjs.cmd.push(async () => {
        pbjs.addAdUnits([
          this.createAdUnit(SKIPPABLE, bids, { skip: 0 }),
          this.createAdUnit(UNSKIPPABLE, bids, { skip: 0 }),
        ]);
        const bidsBackHandler = (bids: any, timedOut: number, auctionId: string) => {
          console.warn(auctionId, timedOut, bids);
          const { skippable, unskippable } = bids || {};
          const allBids = [].concat(skippable?.bids, unskippable?.bids)
            .filter((bid: IBid['bid']) => !!bid)
            .map(bid => ({ bid, provider: this }));
          resolve(allBids);
        };
        pbjs.requestBids({ bidsBackHandler, adUnitCodes: [SKIPPABLE, UNSKIPPABLE], timeout: 3000 });
      });
    });
  }

  public buildVideoUrl(bid: IBid, noGoogle = false) {
    if (noGoogle) return this.buildSimpleUrl(bid);
    return new Promise(async (resolve, reject) => {
      setTimeout(() => reject('buildVideoUrl timeout.'), 1000);
      const { adUnitCode, adId } = bid.bid!;
      const pbjs = await this.pbjsPromise;
      pbjs.cmd.push(() => {
        const adUnit = pbjs.adUnits.find((u: any) => u.code === adUnitCode);
        const videoUrl = pbjs.adServers.dfp.buildVideoUrl({
          adUnit,
          params: {
            iu: this.iu,
            sz: '1x1|640x360|640x390|640x480',
            ppid: this.kv.getPPID(),
            cust_params: this.kv.getObject({ retryOnError: '0' }),
          }
        });
        pbjs.markWinningBidAsUsed({ adId } as any);
        resolve(videoUrl);
      });
    }) as Promise<string>;
  }

  public cleanup() {
    const pbjs = window.pbjs as any;
    pbjs.removeAdUnit(SKIPPABLE);
    pbjs.removeAdUnit(UNSKIPPABLE);
  }

  protected async buildSimpleUrl(bid: IBid) {
    return bid.bid.vastUrl || 'data:application/xml,' + encodeURIComponent(bid.bid.vastXml || '');
  }

  protected createAdUnit(code: string, bids: any[], { skip }: { skip: 1 | 0 }) {
    const placement = 1;
    const plcmt = 1;
    const pos = 1;
    const defaultSize = [640, 480];
    const position = this.kv.getObject({}).position;
    const rwdd = position === 'reward' ? 1 : 0;
    const gpid = [this.iu.replace(/,\d+/, ''), '/ark_', position, '_', code].join('');
    return {
      code,
      bids,
      sizes: [[1, 1], [640, 360], [640, 390], defaultSize],
      rwdd,
      ortb2Imp: {
        rwdd,
        ext: {
          prebid: { is_rewarded_inventory: rwdd },
          gpid,
          data: { pbadslot: gpid }
        }
      },
      mediaTypes: {
        video: {
          rwdd,
          context: 'instream',
          skip,
          placement,
          pos,
          plcmt,
          startdelay: 0,
          minduration: 1,
          maxduration: 32,
          w: defaultSize[0],
          h: defaultSize[1],
          playerSize: defaultSize,
          linearity: 1,
          delivery: [1, 2],
          api: [1, 2, 3, 4, 5, 6, 7, 8, 9],
          protocols: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14],
          playbackmethod: [3],
          playbackend: 1,
          mimes: ["application/javascript", "video/mp4"].concat(getSupportedMimes()),
        },
      },
    };
  }
}
